import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalController, ToastController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { AppointmentInventory } from 'src/app/core/models/appointment-inventory.model';
import { Appointment } from 'src/app/core/models/appointment.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { InventoryService } from 'src/app/core/services/inventory/inventory.service';
import { AddTruckInventoryModalComponent } from '../add-truck-inventory-modal/add-truck-inventory-modal.component';
import { AddWarehouseInventoryModalComponent } from '../add-warehouse-inventory-modal/add-warehouse-inventory-modal.component';

type AddInventoryModalResponse = OverlayEventDetail<{ forcedModal?: string }>;

@Component({
	selector: 'app-inventory-add-tab',
	templateUrl: './inventory-add-tab.component.html',
	styleUrls: ['./inventory-add-tab.component.scss'],
})
export class InventoryAddTabComponent {
	activeAppointmentSub: any;
	appointment: Appointment;
	toBeAddedItems: AppointmentInventory[];
	addedItems: AppointmentInventory[];
	expandedItems = [] as number[];
	modalConfig: { canToggle: boolean; useTruck: boolean; };

	constructor(
		private activatedRoute: ActivatedRoute,
		private appointmentService: AppointmentService,
		private inventoryService: InventoryService,
		private toastController: ToastController,
		private modalController: ModalController,
	) { }

	async ionViewWillEnter() {
		const id = this.activatedRoute.snapshot.parent.parent.params?.id;
		this.appointment = await this.appointmentService.getLocalAppointment(id);
		this.setItems(this.appointment);
		this.updateItemsAfterSync();
		this.modalConfig = await this.inventoryService.getAddInventoryModalConfig();
	}

	ionViewWillLeave() {
		this.activeAppointmentSub.unsubscribe();
	}

	setItems(appointment: Appointment) {
		this.toBeAddedItems = appointment.inventory
			.filter(i => i.statusId == 4)
			.sort((a, b) => a.id - b.id);

		const serverAddedItems = appointment.inventory
			.filter(i => !!i.id && i.statusId == 1 && i.originalStatusId != 1)
			.sort((a, b) => a.id - b.id);

		const localAddedItems = appointment.inventory
			.filter(i => !i.id && i.statusId == 1 && i.originalStatusId != 1)
			.sort((a, b) => a.timestamp < b.timestamp ? -1 : 1); // Sort oldest to newest

		this.addedItems = serverAddedItems.concat(localAddedItems);
	}

	updateItemsAfterSync() {
		this.activeAppointmentSub = this.appointmentService.activeAppointment$
			.subscribe(newAppointment => {
				if (newAppointment) {
					this.setItems(newAppointment);
				}
			});
	}

	onClickToggleSection(sectionIndex: number) {
		const foundIndex = this.expandedItems.findIndex(index => index === sectionIndex);
		if (foundIndex === -1) {
			this.expandedItems.push(sectionIndex);
			return;
		}

		this.expandedItems.splice(foundIndex, 1);
	}

	async onClickAddInventory(item: AppointmentInventory, forcedModal?: string): Promise<void> {
		const component = (!forcedModal && this.modalConfig.useTruck) || forcedModal === 'TRUCK'
			? AddTruckInventoryModalComponent
			: AddWarehouseInventoryModalComponent;

		const modal = await this.modalController.create({
			component,
			componentProps: {
				appointment: this.appointment,
				prefillOptions: {
					categoryId: item.categoryId,
					equipmentId: item.equipId,
					customerServiceId: item.customerServiceId
				},
				...this.modalConfig,
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();
		modal.onDidDismiss().then(
			async (response: AddInventoryModalResponse) => {
				if (response.data.forcedModal) {
					await this.onClickAddInventory(item, response.data.forcedModal);
				}
			}
		);
	}

	async onClickRemoveToBeAddedInventory(item: AppointmentInventory): Promise<void> {
		const id = this.activatedRoute.snapshot.parent.parent.params?.id;
		await this.inventoryService.deleteToBeAddedInventory(id, item.id);

		await this.toastRemoveSuccess();
	}

	private async toastRemoveSuccess(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Successfully removed inventory.',
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}

	isVisible(sectionIndex: number) {
		return (this.expandedItems.findIndex(index => index === sectionIndex) > -1);
	}
}
